import { useLazyQuery } from '@collinsonx/utils/apollo';
import { Booking } from '@collinsonx/utils/generatedTypes/graphql';
import { getBookingByID } from '@collinsonx/utils/queries';
import { useLogStorage } from '@lib/logging';
import { useEffect } from 'react';

import usePayload from './usePayload';

type FetchBookingDetailsData = {
  getBookingByID: Booking;
};

interface UseBookingDetails {
  bookingDetailsData?: FetchBookingDetailsData;
  loadingBookingDetails: boolean;
}

const useBookingDetails = (): UseBookingDetails => {
  const { bookingId } = usePayload();
  const { setBookingData } = useLogStorage();

  const [
    fetchBookingDetails,
    { data: bookingDetailsData, loading: loadingBookingDetails },
  ] = useLazyQuery<{
    getBookingByID: Booking;
  }>(getBookingByID, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      getBookingById: bookingId,
    },
  });

  useEffect(() => {
    if (!bookingId) return;

    fetchBookingDetails();
  }, [bookingId, fetchBookingDetails]);

  useEffect(() => {
    if (!bookingDetailsData) return;

    const { id, reference, status } = bookingDetailsData.getBookingByID ?? {};

    setBookingData({
      bookingID: id,
      bookingReference: reference,
      bookingStatus: status,
    });
  }, [bookingDetailsData]);

  return {
    bookingDetailsData,
    loadingBookingDetails,
  };
};

export default useBookingDetails;
